import { useEffect, useMemo, useState } from 'react';
import { Download, FileText, RefreshCw, ShieldCheck } from 'lucide-react';
import StudioBrand from './StudioBrand';
import { visibleTrusts } from '../lib/accessApi';
import { downloadPilotEvidencePdf } from '../lib/pilotEvidencePdf';
import './pilotEvidence.css';

const monthValue = date => date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0');
function lastCompletedMonth() {
  const today = new Date();
  return monthValue(new Date(today.getFullYear(), today.getMonth() - 1, 1));
}
function formatNumber(value) { return Number(value || 0).toLocaleString('en-GB'); }
function formatRate(rate, count) {
  return count ? rate + '% of ' + formatNumber(count) + ' responses' : 'No responses recorded';
}
function EvidenceMetric({ label, value, detail }) {
  return <div className="pilot-evidence-metric">
    <span>{label}</span><strong>{value}</strong>{detail && <small>{detail}</small>}
  </div>;
}

export default function PilotEvidenceStudio({ workspaceSwitcher = null, studioAccess = null, studioTrusts = [] }) {
  const scopedTrusts = useMemo(() => visibleTrusts(studioAccess, studioTrusts)
    .filter(trust => trust.id !== 'generic_nhs' && trust.active !== false), [studioAccess, studioTrusts]);
  const [trustId, setTrustId] = useState(() => scopedTrusts[0]?.id || '');
  const [month, setMonth] = useState(lastCompletedMonth);
  const [state, setState] = useState({ loading: false, report: null, error: '' });
  const [downloadError, setDownloadError] = useState('');
  const currentMonth = monthValue(new Date());
  useEffect(() => {
    if (!scopedTrusts.some(trust => trust.id === trustId)) setTrustId(scopedTrusts[0]?.id || '');
  }, [scopedTrusts, trustId]);
  useEffect(() => {
    if (!trustId || !month || month > currentMonth) {
      setState({ loading: false, report: null, error: '' }); return undefined;
    }
    let cancelled = false;
    setState({ loading: true, report: null, error: '' });
    setDownloadError('');
    const params = new URLSearchParams({ trustId, month });
    fetch('/api/studio-pilot-evidence?' + params, { credentials: 'same-origin', cache: 'no-store' })
      .then(async response => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || !payload.ok) throw new Error(payload.error || 'Monthly evidence could not be loaded');
        return payload;
      })
      .then(report => { if (!cancelled) setState({ loading: false, report, error: '' }); })
      .catch(error => { if (!cancelled) setState({ loading: false, report: null, error: error.message }); });
    return () => { cancelled = true; };
  }, [trustId, month, currentMonth]);
  const report = state.report;
  const metric = report?.metrics;
  const download = () => {
    if (!report) return;
    try { setDownloadError(''); downloadPilotEvidencePdf(report); }
    catch (error) { setDownloadError(error.message || 'The PDF could not be generated'); }
  };
  return <div className="app-shell">
    <header className="topbar"><div className="brand"><span className="brand-mark"><ShieldCheck/></span>
      <div><strong><StudioBrand/></strong><small>Monthly usage reporting</small></div>
    </div></header>
    <div className="workspace">
      <aside className="catalogue">{workspaceSwitcher}
        <div className="catalogue-intro"><p className="eyebrow">Adoption and evaluation</p>
          <h1>Usage Reports</h1>
          <p>View and download monthly usage during a pilot and throughout ongoing adoption.</p>
          <p>Reports use anonymous usage analytics, not identifiable patient records.</p>
        </div>
      </aside>
      <main className="review-workspace">
        <div className="role-toolbar"><div><p className="eyebrow">Site reporting</p>
          <h1>Monthly usage report</h1>
          <p>Choose a Trust or site and reporting month. Usage Reports are available to Platform Admins only.</p>
        </div></div>
        <section className="pilot-evidence-controls" aria-label="Report settings">
          <label>Trust / site
            <select value={trustId} onChange={event => setTrustId(event.target.value)} disabled={!scopedTrusts.length}>
              {scopedTrusts.map(trust => <option key={trust.id} value={trust.id}>{trust.shortName || trust.name || trust.id}</option>)}
            </select>
          </label>
          <label>Reporting month
            <input type="month" value={month} max={currentMonth} onChange={event => setMonth(event.target.value)}/>
          </label>
          <button className="button primary" type="button" onClick={download} disabled={!report || state.loading}>
            <Download size={17}/> Download monthly PDF
          </button>
        </section>
        {!scopedTrusts.length && <p role="status">No active site is available for your account.</p>}
        {state.loading && <p className="pilot-evidence-message" role="status"><RefreshCw size={17}/> Loading verified monthly analytics...</p>}
        {state.error && <p className="pilot-evidence-error" role="alert">
          <strong>Usage data unavailable.</strong> {state.error} No report has been produced from missing data.
        </p>}
        {downloadError && <p className="pilot-evidence-error" role="alert">{downloadError}</p>}
        {report && <div className="pilot-evidence-content">
          <div className="pilot-evidence-period">
            <FileText size={20}/><div><strong>{report.trustName} · {report.month}</strong>
              <span>{report.period.complete ? 'Complete calendar month' : 'Current month — partial figures'}
                {' · '}UTC reporting window · generated {new Date(report.generatedAt).toLocaleString('en-GB')}</span>
            </div>
          </div>
          <div className="pilot-evidence-metrics">
            <EvidenceMetric label="Tracked events" value={formatNumber(metric.trackedEvents)} detail="Not unique users"/>
            <EvidenceMetric label="Role guide views" value={formatNumber(metric.roleViews)}/>
            <EvidenceMetric label="Journey starts" value={formatNumber(metric.journeyStarts)} detail="Tracked from 8 October 2026"/>
            <EvidenceMetric label="Reached Journey end" value={formatNumber(metric.journeyEnds)} detail="Event count, not completion rate"/>
            <EvidenceMetric label="Helpfulness" value={metric.helpfulness.positiveRate == null ? 'N/A' : metric.helpfulness.positiveRate + '%'}
              detail={formatRate(metric.helpfulness.positiveRate, metric.helpfulness.responses)}/>
            <EvidenceMetric label="Preparedness" value={metric.preparedness.positiveRate == null ? 'N/A' : metric.preparedness.positiveRate + '%'}
              detail={formatRate(metric.preparedness.positiveRate, metric.preparedness.responses)}/>
          </div>
          <section className="pilot-evidence-note">
            <h2>What this report includes</h2>
            <p>Engagement, Journey activity, responses and languages. Only positively verified, published Trust Journeys appear in the pathway ranking; generic NHS and older unverified Journey starts remain separately counted. Historical starts began on 8 October 2026 and cannot form a conversion funnel.</p>
            <p><strong>Not included:</strong> patient/staff survey results, evidence of clinical benefit, staff-time savings or printing reductions. These require separately governed evaluation data.</p>
            {!metric.trackedEvents && <p><strong>No recorded usage for this month.</strong> This does not establish that nobody saw or used the service; please verify analytics attribution and deployment before interpreting it.</p>}
          </section>
        </div>}
      </main>
    </div>
  </div>;
}
